import { Box, Stack, Typography } from '@mui/material'

/**
 * The empty panel a surface renders instead of a bare "No results" line.
 *
 * Two empties are not the same state and must not read the same. A surface
 * with nothing in it yet needs the action that fills it; a surface filtered
 * down to nothing needs its filters back, and the filters themselves must
 * stay on screen so the cause is visible. Callers pick which by what they
 * pass as `title`, `description` and `action` — this component only refuses
 * to let either render as a shrug.
 *
 * `description` is a sentence, not a label: it names the record or the client
 * this board belongs to, so the state reads as this page rather than any page.
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  dense,
}: {
  icon?: React.ReactNode
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  dense?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: dense ? 4 : 7,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 52,
            height: 52,
            mb: 1.75,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'divider',
            '& svg': { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="subtitle2" component="p" sx={{ fontSize: '0.875rem' }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, maxWidth: 380 }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {action}
        </Stack>
      )}
    </Box>
  )
}
